import { useMemo, useState } from 'react';
import { ZONES_BUNDLE, coordsToZoneSlug } from '@auctionradar/shared';
import type { MarketPriceItem, SqmBand, ZoneEntry } from '@auctionradar/shared';
import { useZonePrices } from '../api/hooks';
import { ZoneMiniMap } from '../components/ZoneMiniMap';

/**
 * Atlas of Zones — operator-terminal redesign.
 *
 * A modern, dark, data-first explorer: a bigger/smoother L1→L2→L3 drill on
 * top, and — for the selected zone — price and location side by side above
 * the fold. The price side is a sqm × construction-decade matrix (every combo
 * visible, no vertical scroll); the location side is a focused minimap drawn
 * from the same ZONES_BUNDLE polygons the /map page uses.
 *
 * Replaces the previous cream-paper / Cormorant editorial treatment.
 */

// ─── Real polygon sources (drives the detail "boundary" dot). ─────────────
const REAL_POLY_SOURCES: ReadonlySet<string> = new Set([
  'cityofathens_geitonies',
  'cityofathens_synoikia',
  'cityofathens_dimotikes_koinotites',
  'spitogatos_concaveman',
  'spitogatos_small_hull',
  'listings_concaveman',
  'l3_envelope',
  'iauction_concaveman',
  'iauction_small_hull',
  'iauction_hull',
  'osm_admin_8',
  'osm_admin_9',
  'osm_legacy',
  'piraeus',
  'piraeus_dk',
  'nominatim_polygon',
]);

const SQM_BANDS: ReadonlyArray<{ key: SqmBand; label: string }> = [
  { key: '30-50', label: '30–50' },
  { key: '50-80', label: '50–80' },
  { key: '80-110', label: '80–110' },
  { key: '110-140', label: '110–140' },
  { key: '140+', label: '140+' },
];

function sortByName(a: ZoneEntry, b: ZoneEntry) {
  return a.name_el.localeCompare(b.name_el, 'el');
}

function formatEuro(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return '—';
  return `€${Math.round(n).toLocaleString('el-GR')}`;
}

/** Chronological sort for year_band strings; '*' (year-blind) leads. */
function yearBandRank(s: string): number {
  if (s === '*') return -1;
  if (s === '<1960') return 1959;
  if (s === '2025+') return 9999;
  const m = s.match(/^(\d{4})-/);
  return m && m[1] ? Number(m[1]) : 0;
}

/** Compact column label: '*'→'Όλα', '<1960'→'‹60', '1985-1990'→"'85", '2025+'→"'25+". */
function compactYear(band: string): string {
  if (band === '*') return 'Όλα';
  if (band === '<1960') return '‹60';
  if (band === '2025+') return "'25+";
  const m = band.match(/^(\d{2})(\d{2})-/);
  return m ? `'${m[2]}` : band;
}

function longYear(band: string): string {
  if (band === '*') return 'Όλα τα έτη';
  if (band === '<1960') return 'πριν το 1960';
  if (band === '2025+') return '2025 και μετά';
  const m = band.match(/^(\d{4})-(\d{4})$/);
  return m ? `${m[1]}–${m[2]}` : band;
}

// ─── Page ──────────────────────────────────────────────────────────────────

export function ZonesPage() {
  const [selectedL1, setSelectedL1] = useState<string | null>(null);
  const [selectedL2, setSelectedL2] = useState<string | null>(null);
  const [selectedL3, setSelectedL3] = useState<string | null>(null);
  const [coordResult, setCoordResult] = useState<{ chain: ZoneEntry[]; error?: string; nearestKm?: number } | null>(null);

  const l1List = useMemo(
    () => ZONES_BUNDLE.zones.filter((z) => z.level === 1).sort(sortByName),
    [],
  );
  const l2List = useMemo(() => {
    if (!selectedL1) return [];
    return ZONES_BUNDLE.zones
      .filter((z) => z.level === 2 && z.parent_slug === selectedL1)
      .sort(sortByName);
  }, [selectedL1]);
  const l3List = useMemo(() => {
    if (!selectedL2) return [];
    return ZONES_BUNDLE.zones
      .filter((z) => z.level === 3 && z.parent_slug === selectedL2)
      .sort(sortByName);
  }, [selectedL2]);

  const l1ChildCounts = useMemo(() => childCounts(2), []);
  const l2ChildCounts = useMemo(() => childCounts(3), []);

  const focused: ZoneEntry | null = useMemo(() => {
    const mostSpecific = selectedL3 ?? selectedL2 ?? selectedL1;
    if (!mostSpecific) return null;
    return ZONES_BUNDLE.zones.find((z) => z.slug === mostSpecific) ?? null;
  }, [selectedL1, selectedL2, selectedL3]);

  const handleL1 = (slug: string) => {
    setSelectedL1((p) => (p === slug ? null : slug));
    setSelectedL2(null);
    setSelectedL3(null);
  };
  const handleL2 = (slug: string) => {
    setSelectedL2((p) => (p === slug ? null : slug));
    setSelectedL3(null);
  };
  const handleL3 = (slug: string) => setSelectedL3((p) => (p === slug ? null : slug));

  // Point-in-polygon: drop coords → drill to the containing zone + show chain.
  const locateCoords = (rawLat: number, rawLng: number) => {
    let lat = rawLat;
    let lng = rawLng;
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      setSelectedL1(null); setSelectedL2(null); setSelectedL3(null);
      setCoordResult({ chain: [], error: 'Δώσε έγκυρες συντεταγμένες: lat, lng (π.χ. 37.99, 23.74)' });
      return;
    }
    // Auto-fix the common lng,lat swap: Attica is lat ~37–38.5, lng ~23–24.5.
    if (lat > 23 && lat < 24.6 && lng > 36.5 && lng < 38.6) {
      const t = lat; lat = lng; lng = t;
    }
    const buildChain = (s: string): ZoneEntry[] => {
      const c: ZoneEntry[] = [];
      let cur: string | null = s;
      while (cur != null) {
        const idx: number | undefined = ZONES_BUNDLE.by_slug[cur];
        if (idx == null) break;
        const z: ZoneEntry = ZONES_BUNDLE.zones[idx]!;
        c.unshift(z);
        cur = z.parent_slug;
      }
      return c;
    };
    const slug = coordsToZoneSlug(lat, lng);
    let chain: ZoneEntry[] = slug ? buildChain(slug) : [];
    let nearestKm: number | undefined;
    const deepest = chain[chain.length - 1];
    // No real L3 polygon directly contains the point (coverage gap, or only the
    // overlapping L1 macros matched) → fall back to the nearest real-L3 zone.
    if (!deepest || deepest.level < 3) {
      const n = nearestRealL3(lat, lng);
      if (n) {
        chain = buildChain(n.zone.slug);
        nearestKm = n.km;
      }
    }
    if (chain.length === 0) {
      setSelectedL1(null);
      setSelectedL2(null);
      setSelectedL3(null);
      setCoordResult({ chain: [], error: 'Καμία ζώνη δεν βρέθηκε κοντά σε αυτό το σημείο.' });
      return;
    }
    setSelectedL1(chain.find((z) => z.level === 1)?.slug ?? null);
    setSelectedL2(chain.find((z) => z.level === 2)?.slug ?? null);
    setSelectedL3(chain.find((z) => z.level === 3)?.slug ?? null);
    setCoordResult(nearestKm != null ? { chain, nearestKm } : { chain });
  };

  return (
    <div className="atlas-term font-plex flex w-full flex-col gap-3 px-6 py-4 text-term-text xl:px-10 lg:h-[calc(100vh-3.5rem)] lg:overflow-hidden">
      <Header />

      <CoordLocator onLocate={locateCoords} result={coordResult} />

      {/* Drill nav */}
      <div className="grid shrink-0 grid-cols-1 gap-3 md:grid-cols-3">
        <DrillColumn
          tag="Macro · I"
          countLabel="περιοχές"
          items={l1List}
          selected={selectedL1}
          onSelect={handleL1}
          getCount={(z) => l1ChildCounts[z.slug] ?? 0}
          showChevron
        />
        <DrillColumn
          tag="Γειτονιά · II"
          countLabel="γειτονιές"
          items={l2List}
          selected={selectedL2}
          onSelect={handleL2}
          getCount={(z) => l2ChildCounts[z.slug] ?? 0}
          emptyHint="Επιλέξτε μακρο-περιοχή"
          animKey={selectedL1 ?? 'empty'}
          showChevron
        />
        <DrillColumn
          tag="Υπο-ζώνη · III"
          countLabel="ζώνες"
          items={l3List}
          selected={selectedL3}
          onSelect={handleL3}
          getCount={() => 0}
          emptyHint="Επιλέξτε γειτονιά"
          animKey={selectedL2 ?? 'empty'}
        />
      </div>

      <AtlasDetail focused={focused} />
    </div>
  );
}

function childCounts(level: 2 | 3): Record<string, number> {
  const map: Record<string, number> = {};
  for (const z of ZONES_BUNDLE.zones) {
    if (z.level === level && z.parent_slug) {
      map[z.parent_slug] = (map[z.parent_slug] ?? 0) + 1;
    }
  }
  return map;
}

// Nearest real-polygon L3 to a point (bbox-center distance). Fallback when no
// polygon directly contains the coordinate (the bundle has no centroids, so we
// use the bbox midpoint; ×111 ≈ km per degree at Attica's latitude).
function nearestRealL3(lat: number, lng: number): { zone: ZoneEntry; km: number } | null {
  let best: ZoneEntry | null = null;
  let bestD = Infinity;
  for (const z of ZONES_BUNDLE.zones) {
    if (z.level !== 3 || !REAL_POLY_SOURCES.has(z.polygon_source) || !z.bbox) continue;
    const [minLng, minLat, maxLng, maxLat] = z.bbox;
    const cLng = (minLng + maxLng) / 2;
    const cLat = (minLat + maxLat) / 2;
    const d = Math.hypot(cLng - lng, cLat - lat);
    if (d < bestD) {
      bestD = d;
      best = z;
    }
  }
  return best ? { zone: best, km: bestD * 111 } : null;
}

// ─── Header ──────────────────────────────────────────────────────────────

function Header() {
  const counts = useMemo(() => {
    let l1 = 0, l2 = 0, l3 = 0;
    for (const z of ZONES_BUNDLE.zones) {
      if (z.level === 1) l1++;
      else if (z.level === 2) l2++;
      else l3++;
    }
    return { l1, l2, l3, total: l1 + l2 + l3 };
  }, []);

  return (
    <header className="flex shrink-0 flex-wrap items-end justify-between gap-4">
      <div>
        <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-term-accent">
          Άτλας Ζωνών · Αττική
        </div>
        <h1 className="mt-1 text-2xl font-semibold tracking-tight text-term-text">
          Ιεραρχία ζωνών &amp; τιμές αγοράς
        </h1>
      </div>
      <div className="flex items-center gap-5 font-mono text-[11px] text-term-muted">
        <Stat label="MACRO" value={counts.l1} />
        <Stat label="ΓΕΙΤΟΝΙΕΣ" value={counts.l2} />
        <Stat label="ΥΠΟ-ΖΩΝΕΣ" value={counts.l3} />
        <span className="text-term-line">|</span>
        <Stat label="ΣΥΝΟΛΟ" value={counts.total} accent />
      </div>
    </header>
  );
}

// ─── Coordinate locator ──────────────────────────────────────────────────
// Paste "lat, lng" → point-in-polygon → drills the Atlas to the containing
// zone and shows the L1 › L2 › L3 chain. Runs entirely client-side off the
// same ZONES_BUNDLE polygons the map uses.
function CoordLocator({
  onLocate,
  result,
}: {
  onLocate: (lat: number, lng: number) => void;
  result: { chain: ZoneEntry[]; error?: string; nearestKm?: number } | null;
}) {
  const [value, setValue] = useState('');
  const submit = () => {
    const nums = value.replace(/,/g, ' ').trim().split(/\s+/).map(Number);
    const [lat, lng] = nums;
    if (nums.length >= 2 && Number.isFinite(lat) && Number.isFinite(lng)) onLocate(lat!, lng!);
  };
  const LEVEL_TAG: Record<number, string> = { 1: 'L1', 2: 'L2', 3: 'L3' };

  return (
    <div className="flex shrink-0 flex-wrap items-center gap-3 rounded-2xl border border-term-line bg-term-surface px-4 py-2.5">
      <span className="font-mono text-[10px] font-bold uppercase tracking-[0.16em] text-term-accent">
        📍 Εντοπισμός με συντεταγμένες
      </span>
      <input
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
        placeholder="lat, lng  ·  π.χ. 37.9921, 23.7430"
        className="w-72 rounded-lg border border-term-line bg-term-surface2 px-3 py-1.5 font-mono text-xs text-term-text placeholder:text-term-faint focus:border-term-accent focus:outline-none"
      />
      <button
        onClick={submit}
        className="rounded-lg border border-term-accent bg-term-accent/10 px-3 py-1.5 font-mono text-xs font-semibold text-term-accent transition-colors hover:bg-term-accent/20"
      >
        Βρες ζώνη
      </button>
      {result && (
        <div className="flex flex-wrap items-center gap-1.5 font-mono text-xs">
          {result.error ? (
            <span className="text-rose-500">{result.error}</span>
          ) : (
            <>
              {result.nearestKm != null && (
                <span className="rounded-full border border-amber-400/60 bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-700">
                  ≈ πλησιέστερη ({result.nearestKm < 1 ? `${Math.round(result.nearestKm * 1000)}m` : `${result.nearestKm.toFixed(1)}km`}) — κανένα πολύγωνο δεν την περιέχει
                </span>
              )}
              {result.chain.map((z, i) => (
                <span key={z.slug} className="flex items-center gap-1.5">
                  {i > 0 && <span className="text-term-faint">›</span>}
                  <span className="text-[9px] text-term-faint">{LEVEL_TAG[z.level]}</span>
                  <span className={i === result.chain.length - 1 ? 'font-semibold text-term-accent' : 'text-term-text'}>
                    {z.name_el}
                  </span>
                </span>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, accent }: { label: string; value: number; accent?: boolean }) {
  return (
    <span className="inline-flex items-baseline gap-1.5">
      <span className="text-[9px] tracking-[0.18em] text-term-faint">{label}</span>
      <span className={`text-sm font-medium ${accent ? 'text-term-accent' : 'text-term-text'}`}>
        {value.toLocaleString('el-GR')}
      </span>
    </span>
  );
}

// ─── Drill column ──────────────────────────────────────────────────────────

type DrillProps = {
  tag: string;
  countLabel: string;
  items: ZoneEntry[];
  selected: string | null;
  onSelect: (slug: string) => void;
  getCount: (z: ZoneEntry) => number;
  emptyHint?: string;
  animKey?: string;
  showChevron?: boolean;
};

function DrillColumn({
  tag, countLabel, items, selected, onSelect, getCount, emptyHint, animKey, showChevron,
}: DrillProps) {
  return (
    <section
      key={animKey}
      className={`flex flex-col overflow-hidden rounded-2xl border border-term-line bg-term-surface transition-colors ${
        animKey !== undefined && animKey !== 'empty' ? 'animate-atlas-reveal' : ''
      }`}
    >
      <header className="flex items-center justify-between gap-2 border-b border-term-line bg-term-surface2 px-4 py-3">
        <span className="font-mono text-[10px] font-bold uppercase tracking-[0.16em] text-term-muted">
          {tag}
        </span>
        <span className="rounded-full border border-term-line bg-term-surface px-2 py-0.5 font-mono text-[9px] text-term-faint">
          {items.length} {countLabel}
        </span>
      </header>

      <div className="term-scroll max-h-[260px] min-h-[110px] overflow-y-auto p-1.5 lg:max-h-[26vh]">
        {items.length === 0 ? (
          <p className="px-4 py-10 text-center text-sm text-term-faint">{emptyHint ?? '—'}</p>
        ) : (
          <ul>
            {items.map((z) => (
              <DrillItem
                key={z.slug}
                zone={z}
                selected={selected === z.slug}
                onSelect={onSelect}
                count={getCount(z)}
                showChevron={!!showChevron}
              />
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

function DrillItem({
  zone, selected, onSelect, count, showChevron,
}: {
  zone: ZoneEntry;
  selected: boolean;
  onSelect: (slug: string) => void;
  count: number;
  showChevron?: boolean;
}) {
  return (
    <li>
      <button
        onClick={() => onSelect(zone.slug)}
        className={`group relative flex w-full items-center gap-2 rounded-xl py-2.5 pl-4 pr-3 text-left transition-all duration-200 ${
          selected
            ? 'bg-term-accent-soft text-term-text'
            : 'text-term-muted hover:translate-x-0.5 hover:bg-term-surface2 hover:text-term-text'
        }`}
      >
        {/* Glide-in accent bar (height animates 0 → full). */}
        <span
          aria-hidden
          className={`absolute left-0 top-1/2 w-[3px] -translate-y-1/2 rounded-full bg-term-accent transition-all duration-200 ease-out ${
            selected ? 'h-5' : 'h-0'
          }`}
        />
        <span className={`flex-1 truncate text-[15px] leading-tight ${selected ? 'font-semibold' : ''}`}>
          {zone.name_el}
        </span>
        {count > 0 && (
          <span className="font-mono text-[10px] text-term-faint">{count}</span>
        )}
        {showChevron && (
          <span
            aria-hidden
            className={`text-sm text-term-accent transition-all duration-200 ${
              selected ? 'translate-x-0 opacity-90' : '-translate-x-1 opacity-0'
            }`}
          >
            ›
          </span>
        )}
      </button>
    </li>
  );
}

// ─── Detail ──────────────────────────────────────────────────────────────

function AtlasDetail({ focused }: { focused: ZoneEntry | null }) {
  if (!focused) {
    return (
      <div className="flex flex-1 items-center justify-center rounded-2xl border border-dashed border-term-line bg-term-surface/40">
        <p className="text-sm text-term-faint">Επιλέξτε μια ζώνη για τιμές και τοποθεσία.</p>
      </div>
    );
  }

  const isReal = REAL_POLY_SOURCES.has(focused.polygon_source);
  const levelLabel = focused.level === 1 ? 'Macro' : focused.level === 2 ? 'Γειτονιά' : 'Υπο-ζώνη';

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* Header strip */}
      <div className="shrink-0">
        <div className="mb-1.5 flex flex-wrap items-center gap-x-3 gap-y-1">
          <Breadcrumb zone={focused} />
          <span className="rounded-full border border-term-line px-2 py-0.5 font-mono text-[9px] uppercase tracking-wider text-term-muted">
            L{focused.level} · {levelLabel}
          </span>
          <span
            className="inline-flex items-center gap-1.5 font-mono text-[10px] text-term-faint"
            title={`polygon_source: ${focused.polygon_source}`}
          >
            <span className={`h-1.5 w-1.5 rounded-full ${isReal ? 'bg-term-accent' : 'bg-term-line'}`} />
            {isReal ? 'όριο' : 'σημείο'}
          </span>
        </div>
        <h2 className="mb-3 text-2xl font-semibold tracking-tight text-term-text">{focused.name_el}</h2>
      </div>

      <div className="min-h-0 flex-1">
        <MarketSection focused={focused} />
      </div>
    </div>
  );
}

function Breadcrumb({ zone }: { zone: ZoneEntry }) {
  const chain = useMemo(() => {
    const out: string[] = [];
    let cur: string | null = zone.slug;
    let safety = 0;
    while (cur && safety < 5) {
      const z: ZoneEntry | undefined = ZONES_BUNDLE.zones.find((x) => x.slug === cur);
      if (!z) break;
      out.unshift(z.name_el);
      cur = z.parent_slug;
      safety++;
    }
    return out;
  }, [zone.slug]);
  return (
    <nav className="flex items-center gap-1.5 font-mono text-[11px] text-term-muted">
      {chain.map((name, i) => (
        <span key={i} className="flex items-center gap-1.5">
          {i > 0 && <span className="text-term-line">›</span>}
          <span className={i === chain.length - 1 ? 'text-term-text' : ''}>{name}</span>
        </span>
      ))}
    </nav>
  );
}

// ─── Market section (split: price matrix | minimap) ────────────────────────

function priceSlugsFor(z: ZoneEntry): { own: string | null; parent: string | null } {
  if (z.level === 1) return { own: null, parent: null };
  if (z.level === 2) return { own: z.slug, parent: null };
  return { own: z.slug, parent: z.parent_slug };
}

function MarketSection({ focused }: { focused: ZoneEntry }) {
  const { own: ownSlug, parent: parentSlug } = priceSlugsFor(focused);

  // L3 minimap shows its own geometry with the L2 parent outline behind it;
  // L2 omits the (huge) macro outline.
  const mapParent = useMemo(
    () =>
      focused.level === 3 && focused.parent_slug
        ? ZONES_BUNDLE.zones.find((z) => z.slug === focused.parent_slug) ?? null
        : null,
    [focused],
  );
  const priceParentZone = useMemo(
    () => (parentSlug ? ZONES_BUNDLE.zones.find((z) => z.slug === parentSlug) ?? null : null),
    [parentSlug],
  );

  const ownQuery = useZonePrices(ownSlug);
  const parentQuery = useZonePrices(parentSlug);
  const ownRows = ownQuery.data?.items ?? [];
  const parentRows = parentQuery.data?.items ?? [];
  const usingOwn = ownRows.length > 0;
  const isInherited = !usingOwn && parentRows.length > 0;
  const rows = usingOwn ? ownRows : parentRows;
  const isLoading = ownQuery.isLoading || (!!parentSlug && parentQuery.isLoading && !usingOwn);

  // (sqmBand → yearBand → row)
  const grid = useMemo(() => {
    const map = new Map<string, Map<string, MarketPriceItem>>();
    for (const r of rows) {
      let inner = map.get(r.sqmBand);
      if (!inner) { inner = new Map(); map.set(r.sqmBand, inner); }
      inner.set(r.yearBand, r);
    }
    return map;
  }, [rows]);

  // Year-band columns = union of present bands, '*' first then chronological.
  const yearCols = useMemo(() => {
    const set = new Set<string>();
    for (const r of rows) set.add(r.yearBand);
    return [...set].sort((a, b) => yearBandRank(a) - yearBandRank(b));
  }, [rows]);

  // Selection (with graceful fallback when the stored combo has no row).
  const [selSqm, setSelSqm] = useState<SqmBand>('80-110');
  const [selYear, setSelYear] = useState<string>('*');
  const sqmHasData = (b: SqmBand) => grid.has(b);
  const effSqm: SqmBand = sqmHasData(selSqm)
    ? selSqm
    : (SQM_BANDS.find((b) => sqmHasData(b.key))?.key ?? '80-110');
  const innerForSqm = grid.get(effSqm);
  const effYear = innerForSqm?.has(selYear)
    ? selYear
    : innerForSqm?.has('*')
      ? '*'
      : (innerForSqm ? [...innerForSqm.keys()].sort((a, b) => yearBandRank(a) - yearBandRank(b))[0] ?? '*' : '*');
  const selectedRow = grid.get(effSqm)?.get(effYear) ?? null;

  const selectCell = (sqm: SqmBand, year: string) => { setSelSqm(sqm); setSelYear(year); };

  return (
    <div className="grid h-full min-h-0 grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,460px)] xl:grid-cols-[minmax(0,1fr)_minmax(0,40%)]">
      {/* Left — prices (scrolls internally so the page stays one viewport) */}
      <div className="term-scroll flex min-h-0 flex-col gap-3 lg:overflow-y-auto lg:pr-1">
        {focused.level === 1 ? (
          <Panel>
            <p className="text-sm text-term-muted">
              Οι μακρο-περιοχές δεν τιμολογούνται ως ενιαία μονάδα. Επιλέξτε γειτονιά (L2) ή
              υπο-ζώνη (L3) για ανάλυση τιμών.
            </p>
          </Panel>
        ) : isLoading ? (
          <Panel><p className="text-sm text-term-faint">Φόρτωση τιμών…</p></Panel>
        ) : rows.length === 0 ? (
          <Panel><p className="text-sm text-term-faint">Δεν υπάρχουν δεδομένα τιμών για αυτή τη ζώνη ακόμα.</p></Panel>
        ) : (
          <>
            {isInherited && priceParentZone && (
              <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-term-accent">
                Δεδομένα από: <span className="text-term-text">{priceParentZone.name_el}</span>
              </div>
            )}
            <SelectedHeadline row={selectedRow} sqm={effSqm} year={effYear} />
            <Panel>
              <PriceMatrix
                grid={grid}
                yearCols={yearCols}
                selSqm={effSqm}
                selYear={effYear}
                onSelect={selectCell}
              />
            </Panel>
            {selectedRow && (
              <Panel>
                <SourceBreakdown row={selectedRow} />
              </Panel>
            )}
          </>
        )}
      </div>

      {/* Right — minimap */}
      <div className="min-h-0 overflow-hidden rounded-2xl border border-term-line bg-term-surface2">
        <div className="h-[340px] w-full lg:h-full">
          <ZoneMiniMap zone={focused} parent={mapParent} />
        </div>
      </div>
    </div>
  );
}

function Panel({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-2xl border border-term-line bg-term-surface p-4">{children}</div>
  );
}

// ─── Selected-combo headline ───────────────────────────────────────────────

function SelectedHeadline({
  row, sqm, year,
}: {
  row: MarketPriceItem | null;
  sqm: SqmBand;
  year: string;
}) {
  const band = SQM_BANDS.find((b) => b.key === sqm)?.label ?? sqm;
  return (
    <div className="rounded-2xl border border-term-line bg-gradient-to-br from-term-surface to-term-surface2 p-4">
      <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-term-faint">
        Εκτιμώμενη τιμή · {band} τ.μ.{year !== '*' && <span className="text-term-accent"> · {longYear(year)}</span>}
      </div>
      <div className="mt-1.5 flex flex-wrap items-baseline gap-x-4 gap-y-1">
        <div className="font-mono text-4xl font-semibold tracking-tight text-term-text">
          {formatEuro(row?.pricePerSqmMid)}
          <span className="ml-1 text-base font-medium text-term-muted">/ m²</span>
        </div>
        <div className="text-xs text-term-muted">
          εύρος <b className="text-term-text">{formatEuro(row?.pricePerSqmLow)}</b> – <b className="text-term-text">{formatEuro(row?.pricePerSqmHigh)}</b>
        </div>
        {row?.confidenceScore != null && (
          <div className="ml-auto flex items-center gap-1.5 text-xs text-term-muted">
            confidence
            <ConfidenceDots score={row.confidenceScore} />
            {Math.round(row.confidenceScore * 100)}%
          </div>
        )}
      </div>
    </div>
  );
}

function ConfidenceDots({ score }: { score: number }) {
  const filled = Math.max(0, Math.min(5, Math.round(score * 5)));
  return (
    <span className="inline-flex gap-0.5">
      {Array.from({ length: 5 }, (_, i) => (
        <span
          key={i}
          className={`h-1.5 w-1.5 rounded-full ${i < filled ? 'bg-term-accent' : 'bg-term-line'}`}
        />
      ))}
    </span>
  );
}

// ─── Price matrix (sqm × decade) ───────────────────────────────────────────

function PriceMatrix({
  grid, yearCols, selSqm, selYear, onSelect,
}: {
  grid: Map<string, Map<string, MarketPriceItem>>;
  yearCols: string[];
  selSqm: SqmBand;
  selYear: string;
  onSelect: (sqm: SqmBand, year: string) => void;
}) {
  return (
    <div>
      <div className="mb-2 font-mono text-[10px] uppercase tracking-[0.18em] text-term-faint">
        Τιμές €/m² — μέγεθος × δεκαετία κατασκευής
      </div>
      <div className="term-scroll -mx-1 overflow-x-auto px-1">
        <table className="w-full border-collapse font-mono text-sm">
          <thead>
            <tr>
              <th className="sticky left-0 bg-term-surface px-2 py-2 text-left text-[9px] font-semibold uppercase tracking-wider text-term-faint">
                τ.μ.
              </th>
              {yearCols.map((y) => (
                <th
                  key={y}
                  title={longYear(y)}
                  className={`px-2.5 py-2 text-right text-[10px] font-semibold uppercase tracking-wider ${
                    y === '*' ? 'text-term-accent' : 'text-term-faint'
                  }`}
                >
                  {compactYear(y)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {SQM_BANDS.map((b) => {
              const inner = grid.get(b.key);
              const rowActive = b.key === selSqm;
              return (
                <tr key={b.key} className="border-t border-term-line/60">
                  <td
                    className={`sticky left-0 bg-term-surface px-2 py-2 text-left text-xs ${
                      rowActive ? 'font-semibold text-term-text' : 'text-term-muted'
                    }`}
                  >
                    {b.label}
                  </td>
                  {yearCols.map((y) => {
                    const cell = inner?.get(y);
                    const active = rowActive && y === selYear;
                    return (
                      <td key={y} className="px-1 py-1 text-right">
                        {cell?.pricePerSqmMid != null ? (
                          <button
                            onClick={() => onSelect(b.key, y)}
                            className={`w-full rounded-md px-1.5 py-1.5 text-right tabular-nums transition-colors ${
                              active
                                ? 'bg-term-accent-soft font-bold text-term-text shadow-[inset_0_0_0_1px_theme(colors.term.accent)]'
                                : 'text-term-text hover:bg-term-surface2'
                            }`}
                          >
                            {formatEuro(cell.pricePerSqmMid)}
                          </button>
                        ) : (
                          <span className="block px-1.5 py-1.5 text-term-faint">·</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Source breakdown ──────────────────────────────────────────────────────

function SourceBreakdown({ row }: { row: MarketPriceItem }) {
  const sources: Array<{
    label: string;
    sub: string;
    raw: number | null;
    adjusted?: number | null | undefined;
    count?: number | undefined;
    note?: string | undefined;
  }> = [
    { label: 'Spitogatos', sub: 'asking → adj ×0.80', raw: row.spitogatosAsking, adjusted: row.spitogatosAdjusted, count: row.spitogatosCount },
    { label: 'xe.gr', sub: 'asking → adj', raw: row.xeAsking, adjusted: row.xeAdjusted, count: row.xeCount },
    { label: 'iauction R1', sub: 'appraised', raw: row.iauctionR1Appraised, count: row.iauctionR1Count },
    { label: 'iauction R3+', sub: 'stressed', raw: row.iauctionR3Stressed, count: row.iauctionR3Count },
    { label: 'RE/MAX', sub: 'closing', raw: row.remaxClosing, count: row.remaxCount },
    { label: 'Govt Price Map', sub: 'live summer 2026', raw: row.pricemapGovt, note: row.pricemapGovt == null ? 'pending' : undefined },
  ];

  return (
    <div>
      <div className="mb-2 font-mono text-[10px] uppercase tracking-[0.18em] text-term-faint">
        Ανάλυση κατά πηγή
      </div>
      <ul>
        {sources.map((s) => {
          const has = s.raw != null;
          return (
            <li
              key={s.label}
              className={`grid grid-cols-12 items-baseline gap-2 border-b border-term-line/60 py-2 last:border-0 ${has ? '' : 'opacity-40'}`}
            >
              <div className="col-span-5">
                <div className="text-sm text-term-text">{s.label}</div>
                <div className="font-mono text-[10px] text-term-faint">{s.sub}</div>
              </div>
              <div className="col-span-5 text-right">
                {has ? (
                  <div className="font-mono text-sm font-medium tabular-nums text-term-text">
                    {formatEuro(s.raw)}
                    {s.adjusted != null && s.adjusted !== s.raw && (
                      <span className="ml-1.5 text-term-accent">→ {formatEuro(s.adjusted)}</span>
                    )}
                  </div>
                ) : (
                  <span className="text-xs italic text-term-faint">{s.note ?? '—'}</span>
                )}
              </div>
              <div className="col-span-2 text-right font-mono text-[10px] text-term-faint">
                {s.count != null && s.count > 0 ? `n=${s.count.toLocaleString('el-GR')}` : ''}
              </div>
            </li>
          );
        })}
      </ul>

      {/* Weighted mean */}
      <div className="mt-3 flex items-baseline justify-between border-t-2 border-term-accent/40 pt-3">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-term-accent">
            Σταθμισμένη μέση τιμή
          </div>
          <div className="font-mono text-[10px] text-term-faint">weighted mean</div>
        </div>
        <div className="text-right">
          <div className="font-mono text-2xl font-semibold tabular-nums text-term-accent">
            {formatEuro(row.pricePerSqmMid)}
          </div>
          <div className="font-mono text-[10px] text-term-faint">
            low {formatEuro(row.pricePerSqmLow)} · high {formatEuro(row.pricePerSqmHigh)}
          </div>
        </div>
      </div>
    </div>
  );
}
